import React, { useState, useEffect } from 'react';
import { Shield, Users, Mail, Phone, Calendar, BookOpen, LogOut, Eye, EyeOff, Settings, UserCog } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import UsersManagement from '../components/UsersManagement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminRole, setAdminRole] = useState('');
  const [adminName, setAdminName] = useState('');
  const [currentView, setCurrentView] = useState('contacts'); // 'contacts' or 'users'
  const [showSettings, setShowSettings] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);

  useEffect(() => {
    // Check if already authenticated
    const token = localStorage.getItem('admin_token');
    if (token) {
      setIsAuthenticated(true);
      fetchSubmissions(token);
      fetchAdminProfile(token);
    }
  }, []);

  const fetchAdminProfile = async (token) => {
    try {
      const response = await axios.get(`${API}/admin/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAdminEmail(response.data.email);
      setAdminRole(response.data.role);
      setAdminName(response.data.name || '');
    } catch (error) {
      console.error('Error fetching admin profile:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, {
        password: password
      });

      if (response.data.success) {
        const token = response.data.token;
        localStorage.setItem('admin_token', token);
        setIsAuthenticated(true);
        setAdminEmail(response.data.email);
        toast.success('Connexion réussie!');
        fetchSubmissions(token);
        fetchAdminProfile(token);
      }
    } catch (error) {
      toast.error('Mot de passe incorrect');
    } finally {
      setIsLoading(false);
      setPassword('');
    }
  };

  const fetchSubmissions = async (token) => {
    try {
      const response = await axios.get(`${API}/contact-submissions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setSubmissions([]);
    setAdminEmail('');
    toast.info('Déconnexion réussie');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        // Update token
        localStorage.setItem('admin_token', response.data.new_token);
        toast.success('Mot de passe modifié avec succès!');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowSettings(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(date);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0039A6] to-[#007A5E] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-[#0039A6] rounded-full flex items-center justify-center">
                <Shield className="text-white" size={32} />
              </div>
            </div>
            <CardTitle className="text-2xl text-center">Dashboard Administrateur</CardTitle>
            <p className="text-center text-gray-600">Centre Linguistique Russe du Cameroun</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Entrez le mot de passe"
                    required
                    className="border-gray-300 focus:border-[#0039A6] focus:ring-[#0039A6] pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#0039A6] hover:bg-[#0052d4] text-white py-6"
              >
                {isLoading ? 'Connexion...' : 'Se connecter'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-[#0039A6]">Dashboard {adminRole === 'super_admin' ? 'Super ' : ''}Administrateur</h1>
              <p className="text-sm text-gray-600">{adminName ? `${adminName} - ` : ''}{adminEmail}</p>
            </div>
            <div className="flex gap-3">
              {adminRole === 'super_admin' && (
                <Button
                  onClick={() => setCurrentView(currentView === 'contacts' ? 'users' : 'contacts')}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <UserCog size={18} />
                  {currentView === 'contacts' ? 'Gérer Admins' : 'Voir Contacts'}
                </Button>
              )}
              <Button
                onClick={() => setShowSettings(true)}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Settings size={18} />
                Paramètres
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="flex items-center gap-2"
              >
                <LogOut size={18} />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      {currentView === 'contacts' && (
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#0039A6] rounded-lg flex items-center justify-center">
                  <Users className="text-white" size={24} />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Contacts</p>
                  <p className="text-3xl font-bold text-gray-900">{submissions.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#007A5E] rounded-lg flex items-center justify-center">
                  <Mail className="text-white" size={24} />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Aujourd'hui</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {submissions.filter(s => {
                      const today = new Date().toDateString();
                      const subDate = new Date(s.created_at).toDateString();
                      return today === subDate;
                    }).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#D52B1E] rounded-lg flex items-center justify-center">
                  <BookOpen className="text-white" size={24} />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Cette Semaine</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {submissions.filter(s => {
                      const weekAgo = new Date();
                      weekAgo.setDate(weekAgo.getDate() - 7);
                      return new Date(s.created_at) >= weekAgo;
                    }).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Submissions Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Toutes les Demandes de Contact</CardTitle>
          </CardHeader>
          <CardContent>
            {submissions.length === 0 ? (
              <div className="text-center py-12">
                <Users size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">Aucune demande de contact pour le moment</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-4 font-semibold text-gray-700">Date</th>
                      <th className="text-left p-4 font-semibold text-gray-700">Nom</th>
                      <th className="text-left p-4 font-semibold text-gray-700">Email</th>
                      <th className="text-left p-4 font-semibold text-gray-700">Téléphone</th>
                      <th className="text-left p-4 font-semibold text-gray-700">Cours</th>
                      <th className="text-left p-4 font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((submission) => (
                      <tr key={submission.id} className="border-b hover:bg-gray-50 transition-colors">
                        <td className="p-4">
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar size={14} className="text-gray-400" />
                            {formatDate(submission.created_at)}
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="font-medium text-gray-900">{submission.name}</span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2 text-sm">
                            <Mail size={14} className="text-gray-400" />
                            <a href={`mailto:${submission.email}`} className="text-[#0039A6] hover:underline">
                              {submission.email}
                            </a>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2 text-sm">
                            <Phone size={14} className="text-gray-400" />
                            <a href={`tel:${submission.phone}`} className="text-[#0039A6] hover:underline">
                              {submission.phone}
                            </a>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="text-sm bg-[#007A5E] text-white px-3 py-1 rounded-full">
                            {submission.course || 'Non spécifié'}
                          </span>
                        </td>
                        <td className="p-4">
                          <Button
                            onClick={() => setSelectedSubmission(submission)}
                            size="sm"
                            variant="outline"
                            className="flex items-center gap-2"
                          >
                            <Eye size={14} />
                            Voir
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* Modal for viewing full message */}
      {selectedSubmission && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedSubmission(null)}
        >
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle>Détails de la Demande</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-700">Nom Complet</label>
                <p className="text-gray-900 mt-1">{selectedSubmission.name}</p>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Email</label>
                <p className="text-gray-900 mt-1">
                  <a href={`mailto:${selectedSubmission.email}`} className="text-[#0039A6] hover:underline">
                    {selectedSubmission.email}
                  </a>
                </p>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Téléphone</label>
                <p className="text-gray-900 mt-1">
                  <a href={`tel:${selectedSubmission.phone}`} className="text-[#0039A6] hover:underline">
                    {selectedSubmission.phone}
                  </a>
                </p>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Cours d'intérêt</label>
                <p className="text-gray-900 mt-1">{selectedSubmission.course || 'Non spécifié'}</p>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Message</label>
                <p className="text-gray-900 mt-1 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
                  {selectedSubmission.message}
                </p>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Date de soumission</label>
                <p className="text-gray-900 mt-1">{formatDate(selectedSubmission.created_at)}</p>
              </div>
              <Button
                onClick={() => setSelectedSubmission(null)}
                className="w-full bg-[#0039A6] hover:bg-[#0052d4]"
              >
                Fermer
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Users Management (Super Admin Only) */}
      {currentView === 'users' && adminRole === 'super_admin' && (
        <div className="container mx-auto px-4 py-8">
          <UsersManagement token={localStorage.getItem('admin_token')} />
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowSettings(false)}
        >
          <Card className="max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings size={24} className="text-[#0039A6]" />
                Paramètres du Compte
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <Label className="text-sm font-semibold text-gray-700">Email Administrateur</Label>
                  <p className="text-gray-900 mt-1 bg-gray-50 p-3 rounded">{adminEmail}</p>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-4">Changer le Mot de Passe</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="currentPassword">Mot de passe actuel</Label>
                      <div className="relative">
                        <Input
                          id="currentPassword"
                          type={showCurrentPwd ? "text" : "password"}
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                          required
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPwd(!showCurrentPwd)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                        >
                          {showCurrentPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="newPassword">Nouveau mot de passe</Label>
                      <div className="relative">
                        <Input
                          id="newPassword"
                          type={showNewPwd ? "text" : "password"}
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                          required
                          minLength={8}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPwd(!showNewPwd)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                        >
                          {showNewPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Minimum 8 caractères</p>
                    </div>

                    <div>
                      <Label htmlFor="confirmPassword">Confirmer le nouveau mot de passe</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    className="flex-1 bg-[#0039A6] hover:bg-[#0052d4]"
                  >
                    Enregistrer
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowSettings(false);
                      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    }}
                    className="flex-1"
                  >
                    Annuler
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
